import { useCallback, useEffect, useRef, useState } from "react";

import { Center, Stack } from "@chakra-ui/react";
import { Button } from "@ovou-inc/ovou-ui/design-system/Button";
import queryString from "query-string";
import { useHistory } from "react-router-dom";

import { getTeamsActivation } from "../../../api/teamsActivation";
import {
  setProfile,
  setTeam,
  usePermissionsState,
  useUserDispatch,
} from "../../../context";
import {
  useMyTeamsQuery,
  useProfileDataQuery,
} from "../../../features/auth/queries";
import { MANAGE_USERS_ROUTE } from "../../../constants";
import { removeToken, setToken } from "../../../utils";
import {
  clearTeamsActivationPending,
  getTeamsPurchaseSeats,
  isTeamsPurchaseArrival,
  markTeamsActivationPending,
  TEAMS_ACTIVATION_COPY,
  TEAMS_PURCHASE_SEATS_PARAM,
  type TeamsActivationOutcome,
  waitForTeamsActivation,
} from "../../../utils/teamsActivation";
import { IProfileResponse } from "types/Profile";
import { ITeamResponse } from "types/Team";
import { Text } from "../../../components";
import { Loader } from "../../../components/Spinner";

const LoginWithTokenPage = () => {
  const history = useHistory();
  const dispatch = useUserDispatch();
  const { status: userStatus } = usePermissionsState();
  const [activationProblem, setActivationProblem] = useState<Exclude<
    TeamsActivationOutcome,
    "ready"
  > | null>(null);
  const activationWait = useRef<AbortController | null>(null);

  const { getProfileData } = useProfileDataQuery({
    onSuccess: (data: IProfileResponse) => {
      dispatch(setProfile(data?.profiles?.[0]));
    },
  });
  const {
    data: teamsData,
    getMyTeams,
    isSuccess: wasGetTeamsDataSuccesful,
  } = useMyTeamsQuery({
    onSuccess: (data: ITeamResponse) => {
      const team = data?.teams?.[0];
      dispatch(setTeam(team));
    },
  });

  const { accessToken, refreshToken } = queryString.parse(
    history.location.search,
  );
  const isPurchaseArrival = isTeamsPurchaseArrival(history.location.search);
  const purchaseSeats = getTeamsPurchaseSeats(history.location.search);

  // A Teams purchase can arrive before the team exists: wait for it before
  // reading the teams, so the no-team branch below does not log the buyer out.
  const waitForTeamThenLoad = useCallback(async () => {
    activationWait.current?.abort();
    const wait = new AbortController();
    activationWait.current = wait;
    setActivationProblem(null);
    markTeamsActivationPending();
    const outcome = await waitForTeamsActivation(
      getTeamsActivation,
      wait.signal,
    );
    if (wait.signal.aborted) return;
    clearTeamsActivationPending();
    if (outcome !== "ready") {
      setActivationProblem(outcome);
      return;
    }
    await getProfileData();
    await getMyTeams();
  }, [getProfileData, getMyTeams]);

  useEffect(() => {
    const login = async () => {
      localStorage.clear();
      setToken(accessToken, refreshToken);
      if (isPurchaseArrival) {
        await waitForTeamThenLoad();
        return;
      }
      await getProfileData();
      await getMyTeams();
    };
    if (accessToken && refreshToken) {
      login();
    }
  }, [history, refreshToken, accessToken, getProfileData, getMyTeams]);

  // Leaving mid-wait stops the polling, and must not keep the session's
  // no-team logout switched off.
  useEffect(
    () => () => {
      activationWait.current?.abort();
      clearTeamsActivationPending();
    },
    [],
  );

  useEffect(() => {
    if (wasGetTeamsDataSuccesful && userStatus) {
      const team = teamsData?.teams?.[0];
      if (!team) {
        removeToken();
        history.replace("/auth/logout", { notOwner: true });
      } else if (isPurchaseArrival) {
        history.replace(
          purchaseSeats
            ? `${MANAGE_USERS_ROUTE}?${TEAMS_PURCHASE_SEATS_PARAM}=${purchaseSeats}`
            : MANAGE_USERS_ROUTE,
        );
      } else {
        history.replace("/");
      }
    }
  }, [wasGetTeamsDataSuccesful, userStatus]);

  if (activationProblem) {
    return (
      <Center height="full" padding="6">
        <Stack spacing="4" alignItems="center" maxWidth="md" textAlign="center">
          <Text as="p" variant="body">
            {activationProblem === "refused"
              ? TEAMS_ACTIVATION_COPY.refused
              : TEAMS_ACTIVATION_COPY.stillWaiting}
          </Text>
          <Button onClick={waitForTeamThenLoad}>
            {TEAMS_ACTIVATION_COPY.tryAgain}
          </Button>
        </Stack>
      </Center>
    );
  }

  return <Loader />;
};

export default LoginWithTokenPage;
